// src/hooks/useFormState.js
// ─────────────────────────────────────────────────────────────
// Custom hook – správa stavu formuláře, autosave, progress
// ─────────────────────────────────────────────────────────────

import { useState, useEffect, useRef, useCallback } from 'react';
import { upsertApplication } from '../lib/api';
import { REQUIRED_KEYS } from '../lib/questions';
import { trackFormStart } from '../lib/analytics';

const STORE_KEY = 'jic_founder_draft';

function generateSessionId() {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 8);
}

export function useFormState() {
  // Inicializace z localStorage
  const [draft, setDraft] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORE_KEY));
      if (stored && stored._sessionId) return stored;
    } catch {}
    return { _sessionId: generateSessionId(), _startedAt: new Date().toISOString() };
  });

  const [saveStatus, setSaveStatus] = useState('idle'); // 'idle'|'saving'|'saved'|'error'
  const [pct, setPct] = useState(0);
  const saveTimerRef = useRef(null);
  const isFirstSave = useRef(true);
  const formStartTracked = useRef(false);

  // ── Výpočet progress ────────────────────────────────────────
  const calcPct = useCallback((data) => {
    let filled = 0;
    REQUIRED_KEYS.forEach(k => {
      if (data[k] && String(data[k]).trim().length > 0) filled++;
    });
    return Math.round((filled / REQUIRED_KEYS.length) * 100);
  }, []);

  // ── Update pole ─────────────────────────────────────────────
  const updateField = useCallback((key, value) => {
    if (!formStartTracked.current && value) {
      formStartTracked.current = true;
      trackFormStart();
    }
    setDraft(prev => {
      const next = { ...prev, [key]: value };
      // Uložit do localStorage ihned
      localStorage.setItem(STORE_KEY, JSON.stringify(next));
      return next;
    });
    setSaveStatus('saving');
    // Debounce Supabase upsert
    clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      try {
        await upsertApplication({ ...draft, [key]: value, session_id: draft._sessionId, status: 'partial' });
        setSaveStatus('saved');
      } catch {
        setSaveStatus('error');
      }
    }, 1500);
  }, [draft]);

  // ── Progress aktualizace ────────────────────────────────────
  useEffect(() => {
    setPct(calcPct(draft));
  }, [draft, calcPct]);

  // ── Registrace session (admin tracking) ─────────────────────
  useEffect(() => {
    if (!isFirstSave.current) return;
    isFirstSave.current = false;
    // Zaregistrujte otevření formuláře v Supabase
    // (i když uživatel formulář nedokončí, admin ho uvidí)
    const registerSession = async () => {
      try {
        await upsertApplication({
          session_id: draft._sessionId,
          status: 'draft',
          created_at: draft._startedAt,
        });
      } catch {}
    };
    registerSession();
  }, []); // eslint-disable-line

  // ── Reset po odeslání ───────────────────────────────────────
  const resetForm = useCallback(() => {
    localStorage.removeItem(STORE_KEY);
    setDraft({ _sessionId: generateSessionId(), _startedAt: new Date().toISOString() });
    setSaveStatus('idle');
  }, []);

  // ── Uložení s příznakem "odesláno" ─────────────────────────
  const markSubmitted = useCallback(async () => {
    const submittedAt = new Date().toISOString();
    const final = { ...draft, status: 'done', _submittedAt: submittedAt };
    localStorage.setItem(STORE_KEY, JSON.stringify(final));
    await upsertApplication({ ...final, submitted_at: submittedAt });
    return final;
  }, [draft]);

  return {
    draft,
    updateField,
    saveStatus,
    pct,
    resetForm,
    markSubmitted,
  };
}
